import { ReactNode, useState, createContext, useContext } from "react";
import * as AccordionPrimitives from "@radix-ui/react-accordion";
import classNames from "classnames";
import { CaretDown } from "components/icons/CaretDown";

type AccordionContextType = {
  openItems: Set<string>;
  toggleItem: (value: string) => void;
};

const AccordionContext = createContext<AccordionContextType | undefined>(
  undefined
);

const useAccordionContext = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error(
      "Accordion components must be used within SettingsAccordion.Root"
    );
  }
  return context;
};

type RootProps = {
  children: ReactNode;
  defaultValue?: string[];
};

const Root = ({ children, defaultValue = [] }: RootProps) => {
  const [openItems, setOpenItems] = useState<Set<string>>(
    new Set(defaultValue)
  );

  const toggleItem = (value: string) => {
    setOpenItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return newSet;
    });
  };

  return (
    <AccordionContext.Provider value={{ openItems, toggleItem }}>
      <div className="t-flex t-flex-col t-gap-2">{children}</div>
    </AccordionContext.Provider>
  );
};

type ItemContextType = {
  value: string;
  isOpen: boolean;
};

const ItemContext = createContext<ItemContextType | undefined>(undefined);

const useItemContext = () => {
  const context = useContext(ItemContext);
  if (!context) {
    throw new Error(
      "Accordion Trigger/Content must be used within SettingsAccordion.Item"
    );
  }
  return context;
};

type ItemProps = {
  value: string;
  children: ReactNode;
};

const Item = ({ value, children }: ItemProps) => {
  const { openItems } = useAccordionContext();
  const isOpen = openItems.has(value);

  return (
    <ItemContext.Provider value={{ value, isOpen }}>
      <div className="t-flex t-flex-col">{children}</div>
    </ItemContext.Provider>
  );
};

type TriggerProps = {
  children: ReactNode;
};

const Trigger = ({ children }: TriggerProps) => {
  const { value, isOpen } = useItemContext();
  const { toggleItem } = useAccordionContext();

  return (
    <button
      type="button"
      onClick={() => toggleItem(value)}
      className="t-flex t-items-center t-gap-2 t-cursor-pointer t-border-0 t-p-0 t-bg-white t-py-2 t-px-1 t-mb-2 hover:t-bg-surface-lighter-grey"
    >
      <CaretDown
        className={classNames(
          "t-transition-transform t-duration-200 t-text-text-30 t-size-4 -t-rotate-90",
          {
            "t-rotate-0": isOpen,
          }
        )}
      />
      {children}
    </button>
  );
};

const Content = (
  props: AccordionPrimitives.AccordionContentProps &
    React.RefAttributes<HTMLDivElement>
) => {
  const { isOpen } = useItemContext();

  if (!isOpen) return null;

  return (
    <div
      {...props}
      className={classNames(
        "t-flex t-flex-col",
        props.className || "t-ml-[3.625rem] sm:t-ml-7"
      )}
    />
  );
};

export const SettingsAccordion = {
  Root,
  Item,
  Trigger,
  Content,
};
